import React, { useMemo, useState } from "react";

export default function RedeemModal({
  cafeLogo,
  cafeLocation,
  cafeSpace,
  cafeName,
  offerTitle,
  onRedeem,
}) {
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const amountNumber = useMemo(() => {
    const cleaned = amount.replace(/[^0-9.]/g, "");
    const n = Number(cleaned);
    if (!Number.isFinite(n)) return null;
    if (n <= 0) return null;
    return n;
  }, [amount]);

  const canRedeem = amountNumber !== null && !submitting;

  async function handleRedeem() {
    if (!canRedeem) return;
    setErr("");
    setSubmitting(true);

    try {
      await onRedeem(amountNumber);
    } catch (e) {
      setErr(e?.message || "Redeem failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.backdrop}>
      <div style={styles.modal}>

        {/* Cafe hero image */}
        {cafeName && (
          <div
            style={{
              ...styles.hero,
              backgroundImage: `url(${cafeSpace})`,
            }}
          >
            <div style={styles.heroOverlay} />

            {cafeLogo && (
              <div style={styles.logoBadge}>
                <div className="circle">
                  <img src={cafeLogo} alt="logo" style={styles.logoImg} />
                </div>
              </div>
            )}

            <div style={styles.heroText}>
              <p style={styles.heroTitle}>{cafeName}</p>
              <p className="cityName">{cafeLocation}</p>
            </div>
          </div>
        )}

        <div style={styles.content}>
          <div style={styles.offer}>{offerTitle}</div>

          <label style={styles.label}>
            Amount you're paying at the register
          </label>

          <div style={styles.inputRow}>
            <span style={{ opacity: 0.7 }}>$</span>
            <input
              style={styles.input}
              inputMode="decimal"
              placeholder="12.50"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>

          {err && <div style={styles.error}>{err}</div>}

          <button
            style={{ ...styles.cta, opacity: canRedeem ? 1 : 0.5 }}
            disabled={!canRedeem}
            onClick={handleRedeem}
          >
            {submitting ? "Redeeming…" : "Redeem"}
          </button>

          <div style={styles.note}>
            After you redeem, this offer disappears from your account.
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "grid",
    placeItems: "center",
    padding: 16,
  },

  modal: {
    width: "100%",
    maxWidth: 480,
    background: "#fff",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
  },

  hero: {
    height: 220,
    backgroundSize: "cover",
    backgroundPosition: "center",
    position: "relative",
  },

  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(0,0,0,0.25)",
  },

  logoBadge: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 50,
    height: 50,
    borderRadius: "50%",
    overflow: "hidden",
    border: "2px solid white",
    background: "#fff",
    zIndex: 2,
  },

  logoImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  circle: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    overflow: "hidden",
  },
  heroText: {
    position: "absolute",
    bottom: 0,
    left: 16,
    color: "#fff",
    zIndex: 2,
    width: "90%",
    paddingRight: 24,
    display: "flex",
    paddingLeft: 12,
    alignItems: "center",
    justifyContent: "space-between",
  },

  heroTitle: {
    fontSize: 22,
    fontWeight: 700,
  },
  cityName: {
    fontSize: 16,
    lineHeight: "0.4rem",
  },
  content: {
    padding: 18,
  },

  offer: {
    fontSize: 16,
    opacity: 0.85,
    marginBottom: 10,
  },

  label: {
    display: "block",
    fontSize: 13,
    marginTop: 10,
    marginBottom: 6,
    opacity: 0.9,
  },

  inputRow: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #ddd",
    borderRadius: 12,
    padding: "10px 12px",
    gap: 8,
  },

  input: {
    width: "100%",
    border: "none",
    outline: "none",
    fontSize: 16,
  },

  error: {
    marginTop: 10,
    color: "#b00020",
    fontSize: 13,
  },

  cta: {
    marginTop: 14,
    width: "100%",
    border: "none",
    borderRadius: 12,
    padding: "12px 14px",
    fontSize: 16,
    cursor: "pointer",
  },

  note: {
    marginTop: 10,
    fontSize: 12,
    opacity: 0.7,
  },
};
